import React, { useEffect, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { AuthProvider, AuthContext } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { VisitorProvider } from './contexts/VisitorContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AuthRedirectRoute from './components/auth/AuthRedirectRoute';
import PublicRouteWrapper from './components/common/PublicRouteWrapper';
import { enforceActiveMenuStyling, setupStyleObserver } from './utils/styleUtils';

// Import theme initializer and style cleaner
import ThemeInitializer from './ThemeInitializer';
import StyleCleaner from './components/StyleCleaner'; 

// Styles
import 'react-toastify/dist/ReactToastify.css';
import './styles/ThemeVariables.css';
import './styles/ThemeOverrides.css';
import './styles/SuperadminTheme.css';
import './App.css';

// SaaS pages
import SaasLanding from './pages/SaasLanding';
import TenantRegistration from './pages/TenantRegistration';

// Admin components
import AdminDashboard from './components/admin/Dashboard';
import AdminSettings from './components/admin/Settings';
import AdminUsers from './components/admin/Users';
// import AdminVillas from './components/admin/Villas';
import AdminUnits from './components/admin/Units';
import UnitDetail from './components/admin/UnitDetail';
import AdminVisitors from './components/admin/Visitors';
import AdminActivity from './components/admin/Activity';
import AdminReports from './components/admin/Reports';
import AdminComplaints from './components/admin/Complaints';
import AdminFinancials from './components/admin/Financials';
import AdminCorpusFund from './components/admin/CorpusFund';
import AdminMembership from './components/admin/Membership';
import AdminRules from './components/admin/Rules';
import AdminAnnouncements from './components/admin/Announcements';
import AdminSupportTickets from './components/admin/SupportTickets';
import RuleForm from './components/admin/RuleForm';
import AnnouncementForm from './components/admin/AnnouncementForm';

// Maintenance components
import MaintenanceDashboard from './components/maintenance/Dashboard';
import MaintenanceComplaints from './components/maintenance/Complaints';
import MaintenanceRules from './components/maintenance/Rules';
import MaintenanceAnnouncements from './components/maintenance/Announcements';

// Guard components
import GuardDashboard from './components/guard/Dashboard';
import GuardVisitors from './components/guard/Visitors';
import VisitorSearch from './components/guard/VisitorSearch';
import { default as GuardVillas } from './components/guard/Villas';
import GuardRules from './components/guard/GuardRules';
import GuardAnnouncements from './components/guard/GuardAnnouncements';
import GuardVehicles from './components/guard/Vehicles';

// Production components

// Owner components
import OwnerDashboard from './components/owner/Dashboard';
import OwnerVisitors from './components/owner/Visitors';
import OwnerProfile from './components/owner/Profile';
import MyVillas from './components/owner/MyVillas';
import VillaDetail from './components/owner/VillaDetail';
import Payments from './components/owner/Payments';
import Complaints from './components/owner/Complaints';
import OwnerRules from './components/owner/OwnerRules';
import OwnerAnnouncements from './components/owner/OwnerAnnouncements';
import Vehicles from './components/owner/Vehicles';


// Auth components
import Login from './components/auth/Login';
// import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import Profile from './components/auth/Profile';
import ChangePassword from './components/auth/ChangePassword';

// Shared components
import Navbar from './components/layout/Navbar';
import NotFound from './components/common/NotFound';
import OrganizationStatusBanner from './components/common/OrganizationStatusBanner';
import DirectoryPage from './components/common/DirectoryPage';
import PrivacyPolicy from './components/common/PrivacyPolicy';
import Support from './components/Support';
import ContactPage from './pages/ContactPage';

// Superadmin components
import SuperadminDashboard from './components/superadmin/Dashboard';
import Tenants from './components/superadmin/Tenants';
import Plans from './components/superadmin/Plans';
import Subscriptions from './components/superadmin/Subscriptions';
import TransactionHistory from './components/superadmin/TransactionHistory';
import SuperadminSettings from './components/superadmin/Settings';
import SupportTickets from './components/superadmin/SupportTickets';
import Testimonials from './components/superadmin/Testimonials';
import SwitchUser, { SwitchUserFAB, SwitchUserBanner } from './components/superadmin/SwitchUser';

import ScrollToTop from './components/ScrollToTop';

// Organization context wrapper
const OrganizationContext = React.createContext(null);
export { OrganizationContext };

// HOC to extract organization from URL
const withOrganizationContext = (Component) => {
  const WithOrganizationContext = (props) => {
    // Extract organization from URL if it exists
    const { orgDomain } = useParams();
    
    React.useEffect(() => {
      if (orgDomain) {
        console.log(`Organization context detected: ${orgDomain}`);
        // You might want to fetch organization details here or in a context provider
      }
    }, [orgDomain]);
    
    return (
      <OrganizationContext.Provider value={{ orgDomain }}>
        <Component {...props} />
      </OrganizationContext.Provider>
    );
  };
  WithOrganizationContext.displayName = `WithOrganizationContext(${Component.displayName || Component.name || 'Component'})`;
  return WithOrganizationContext;
};

// Home redirect component with organization context support
const Home = () => {
  const { currentUser } = useContext(AuthContext);
  const context = useContext(OrganizationContext);
  const orgDomain = context?.orgDomain;
  
  // If SaaS mode is enabled and no user is logged in, show the SaaS landing page
  if (!currentUser) {
    if (process.env.REACT_APP_SAAS_MODE === 'true') {
      return <Navigate to="/landing" replace />;
    }
    return <Navigate to="/login" replace />;
  }
  
  // Prefix with organization domain if available
  const prefix = orgDomain ? `/${orgDomain}` : '';
  
  switch (currentUser.role) {
    case 'admin':
      return <Navigate to={`${prefix}/admin/dashboard`} replace />;
    case 'owner':
      return <Navigate to={`${prefix}/owner/dashboard`} replace />;
    case 'guard':
      return <Navigate to={`${prefix}/guard/dashboard`} replace />;
    case 'maintenance':
      return <Navigate to={`${prefix}/maintenance/dashboard`} replace />;
    case 'superadmin':
      return <Navigate to="/superadmin/dashboard" replace />;
    default:
      return <Navigate to={`${prefix}/login`} replace />;
  }
};

// Apply default styles immediately with inline script
const applyInitialStyles = () => {
  // Get active colors from localStorage if available
  const getLocalStorage = (key, fallback) => {
    try {
      const value = localStorage.getItem(`app_${key}`);
      return value || fallback;
    } catch (e) {
      return fallback;
    }
  };
  
  const sidebarActiveBgColor = getLocalStorage('sidebar_active_bg_color', '#0d6efd');
  const sidebarActiveTextColor = getLocalStorage('sidebar_active_text_color', '#ffffff');
  const sidebarHoverBgColor = getLocalStorage('sidebar_hover_bg_color', '#f8f9fa');
  const sidebarHoverTextColor = getLocalStorage('sidebar_hover_text_color', '#0d6efd');
  const sidebarBgColor = getLocalStorage('sidebar_bg_color', '#ffffff');
  const sidebarTextColor = getLocalStorage('sidebar_text_color', '#212529');
  
  // Inject styles directly
  const styleEl = document.createElement('style');
  styleEl.id = 'initial-sidebar-styles';
  styleEl.innerHTML = `
    /* Force sidebar styles immediately */
    .sidebar-navigation {
      background-color: ${sidebarBgColor} !important;
    }
    .sidebar-navigation .menu-link,
    .sidebar-navigation .menu-text,
    .sidebar-navigation .menu-icon,
    .sidebar-navigation .society-name {
      color: ${sidebarTextColor} !important;
    }
    
    /* Regular hover styles (only for non-active items) */
    .sidebar-navigation .menu-link:hover:not(.active),
    .sidebar-navigation .menu-item:hover:not(.active) > a {
      background-color: ${sidebarHoverBgColor} !important;
    }
    .sidebar-navigation .menu-link:hover:not(.active) .menu-icon,
    .sidebar-navigation .menu-link:hover:not(.active) .menu-text,
    .sidebar-navigation .menu-item:hover:not(.active) > a .menu-icon,
    .sidebar-navigation .menu-item:hover:not(.active) > a .menu-text {
      color: ${sidebarHoverTextColor} !important;
    }
    
    /* Active item styles */
    .sidebar-navigation .menu-item.active,
    .sidebar-navigation li.active,
    .sidebar-navigation .active,
    .sidebar-navigation a.active,
    html body .sidebar-navigation .menu-item.active,
    html body .sidebar-navigation li.active {
      background-color: ${sidebarActiveBgColor} !important;
    }
    
    .sidebar-navigation .menu-item.active *,
    .sidebar-navigation li.active *,
    .sidebar-navigation .active * {
      color: ${sidebarActiveTextColor} !important;
    }
    
    /* Critical: active item hover styles */
    .sidebar-navigation .menu-item.active:hover,
    .sidebar-navigation li.active:hover,
    .sidebar-navigation .active:hover,
    .sidebar-navigation a.active:hover,
    html body .sidebar-navigation .menu-item.active:hover,
    html body .sidebar-navigation li.active:hover {
      background-color: ${sidebarActiveBgColor} !important;
    }
    
    .sidebar-navigation .menu-item.active:hover *,
    .sidebar-navigation li.active:hover *,
    .sidebar-navigation .active:hover *,
    .sidebar-navigation a.active:hover * {
      color: ${sidebarActiveTextColor} !important;
    }
  `;
  document.head.appendChild(styleEl);
  
  // Add delayed reinforce function to ensure styles are applied
  setTimeout(() => {
    // Find active menu items and explicitly set styles
    // const activeItems = document.querySelectorAll('.sidebar-navigation .active, .sidebar-navigation .menu-item.active, .sidebar-navigation li.active');
    // activeItems.forEach(item => {
    //   item.style.backgroundColor = sidebarActiveBgColor;
    //   
    //   // Add hover event listener
    //   item.addEventListener('mouseenter', function() {
    //     this.style.backgroundColor = sidebarActiveBgColor;
    //     const children = this.querySelectorAll('*');
    //     children.forEach(child => {
    //       child.style.color = sidebarActiveTextColor;
    //     });
    //   });
    //   
    //   // Apply to children
    //   const children = item.querySelectorAll('*');
    //   children.forEach(child => {
    //     child.style.color = sidebarActiveTextColor;
    //   });
    // });
  }, 500);
  
  // Note: Settings are now handled by SettingsContext, no need for direct fetch here
};

// Apply styles immediately
applyInitialStyles();

// Organization status banner is already imported at the top

// Higher-order component for layout wrapping
const withLayout = (Component) => {
  const WithLayout = (props) => {
    // Check if user is maintenance role to skip unnecessary components
    const userStr = sessionStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const isMaintenance = user?.role === 'maintenance';
    
    return (
      <>
        <Navbar />
        {/* Only show OrganizationStatusBanner for non-maintenance users */}
        {!isMaintenance && <OrganizationStatusBanner />}
        <div className="content-wrapper">
          <div className="main-content">
            <Component {...props} />
          </div>
        </div>
      </>
    );
  };
  WithLayout.displayName = `WithLayout(${Component.displayName || Component.name || 'Component'})`;
  return WithLayout;
};

// Layout-wrapped components
const AdminDashboardWithLayout = withLayout(AdminDashboard);
const AdminSettingsWithLayout = withLayout(AdminSettings);
const AdminUsersWithLayout = withLayout(AdminUsers);
// const AdminVillasWithLayout = withLayout(AdminVillas);
const AdminUnitsWithLayout = withLayout(AdminUnits);
const AdminComplaintsWithLayout = withLayout(AdminComplaints);
const AdminFinancialsWithLayout = withLayout(AdminFinancials);
const AdminCorpusFundWithLayout = withLayout(AdminCorpusFund);
const AdminVisitorsWithLayout = withLayout(AdminVisitors);
const AdminActivityWithLayout = withLayout(AdminActivity);
const AdminReportsWithLayout = withLayout(AdminReports);
const AdminMembershipWithLayout = withLayout(AdminMembership);
const AdminRulesWithLayout = withLayout(AdminRules);
const AdminAnnouncementsWithLayout = withLayout(AdminAnnouncements);
const AdminSupportTicketsWithLayout = withLayout(AdminSupportTickets);
const RuleFormWithLayout = withLayout(RuleForm);
const AnnouncementFormWithLayout = withLayout(AnnouncementForm);

const GuardDashboardWithLayout = withLayout(GuardDashboard);
const GuardVisitorsWithLayout = withLayout(GuardVisitors);
const VisitorSearchWithLayout = withLayout(VisitorSearch);
const GuardVillasWithLayout = withLayout(GuardVillas);
const GuardRulesWithLayout = withLayout(GuardRules);
const GuardAnnouncementsWithLayout = withLayout(GuardAnnouncements);
const GuardVehiclesWithLayout = withLayout(GuardVehicles);

const OwnerDashboardWithLayout = withLayout(OwnerDashboard);
const OwnerVisitorsWithLayout = withLayout(OwnerVisitors);
const OwnerProfileWithLayout = withLayout(OwnerProfile);
const MyVillasWithLayout = withLayout(MyVillas);
const VillaDetailWithLayout = withLayout(VillaDetail);
const PaymentsWithLayout = withLayout(Payments);
const ComplaintsWithLayout = withLayout(Complaints);
const OwnerRulesWithLayout = withLayout(OwnerRules);
const OwnerAnnouncementsWithLayout = withLayout(OwnerAnnouncements);
const VehiclesWithLayout = withLayout(Vehicles);


// Superadmin components with layout
const SuperadminDashboardWithLayout = withLayout(SuperadminDashboard);
const TenantsWithLayout = withLayout(Tenants);
const PlansWithLayout = withLayout(Plans);
const SubscriptionsWithLayout = withLayout(Subscriptions);
const TransactionHistoryWithLayout = withLayout(TransactionHistory);
const SuperadminSettingsWithLayout = withLayout(SuperadminSettings);
const SupportTicketsWithLayout = withLayout(SupportTickets);
const TestimonialsWithLayout = withLayout(Testimonials);
const SwitchUserWithLayout = withLayout(SwitchUser);

// Define layout component wrappers in the correct section around line 300

// const MaintenanceDashboardWithLayout = withLayout(MaintenanceDashboard);
// const MaintenanceComplaintsWithLayout = withLayout(MaintenanceComplaints);

// Special wrapper for maintenance routes to avoid visitor API calls 
const withMaintenanceLayout = (Component) => {
  const WithMaintenanceLayout = (props) => {
    return (
      <>
        <Navbar />
        <div className="content-wrapper">
          <div className="main-content">
            <Component {...props} />
          </div>
        </div>
      </>
    );
  };
  WithMaintenanceLayout.displayName = `WithMaintenanceLayout(${Component.displayName || Component.name || 'Component'})`;
  return WithMaintenanceLayout;
};

// Create maintenance components with the special layout
const MaintenanceDashboardWithSpecialLayout = withMaintenanceLayout(MaintenanceDashboard);
const MaintenanceComplaintsWithSpecialLayout = withMaintenanceLayout(MaintenanceComplaints);
const MaintenanceRulesWithSpecialLayout = withMaintenanceLayout(MaintenanceRules);
const MaintenanceAnnouncementsWithSpecialLayout = withMaintenanceLayout(MaintenanceAnnouncements);

// Directory page layout wrapper
const DirectoryPageWithLayout = withLayout(DirectoryPage);

const App = () => {
  // Apply active menu styling when the component mounts
  useEffect(() => {
    // Apply immediately
    enforceActiveMenuStyling();
    
    // Set up observer to watch for DOM changes
    const observer = setupStyleObserver();
    
    // Add window events to ensure styling on page load/refresh
    window.addEventListener('load', enforceActiveMenuStyling);
    window.addEventListener('DOMContentLoaded', enforceActiveMenuStyling);
    
    // Apply with a delay to catch any late DOM changes
    const delayedEnforce = setTimeout(() => {
      enforceActiveMenuStyling();
    }, 500);
    
    
    // Cleanup
    return () => {
      observer.disconnect();
      window.removeEventListener('load', enforceActiveMenuStyling);
      window.removeEventListener('DOMContentLoaded', enforceActiveMenuStyling);
      clearTimeout(delayedEnforce);
    };
  }, []);
  
  return (
    <Router>
      <ScrollToTop />
      <AuthProvider>
        <SettingsProvider>
          <ThemeInitializer />
          <StyleCleaner />
          <VisitorProvider>
            <div className="app-container">
              <ToastContainer position="top-right" autoClose={3000} />
              <SwitchUserBanner />
              <SwitchUserFAB />
              <Routes>
                {/* SaaS and public routes */}
                <Route path="/landing" element={<PublicRouteWrapper><SaasLanding /></PublicRouteWrapper>} />
                <Route path="/register" element={
                  <AuthRedirectRoute>
                    <TenantRegistration />
                  </AuthRedirectRoute>
                } />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/support" element={<Support />} />
                <Route path="/login" element={
                  <AuthRedirectRoute>
                    <OrganizationContext.Provider value={null}>
                      <Login />
                    </OrganizationContext.Provider>
                  </AuthRedirectRoute>
                } />
                <Route path="/forgot-password" element={
                  <AuthRedirectRoute>
                    <OrganizationContext.Provider value={null}>
                      <ForgotPassword />
                    </OrganizationContext.Provider>
                  </AuthRedirectRoute>
                } />
                <Route path="/reset-password" element={
                  <OrganizationContext.Provider value={null}>
                    <ResetPassword />
                  </OrganizationContext.Provider>
                } />
                <Route path="/contact" element={<ContactPage />} />

                {/* Root path redirect */}
                <Route path="/" element={<Navigate to="/landing" replace />} />
                
                {/* Organization-specific routes */}
                <Route path="/:orgDomain">
                  {/* Organization login */}
                  <Route path="login" element={
                    <AuthRedirectRoute>
                      {withOrganizationContext(Login)()}
                    </AuthRedirectRoute>
                  } />
                  
                  {/* Organization root redirects to proper dashboard based on role */}
                  <Route path="" element={withOrganizationContext(Home)()} />
                  
                  {/* Protected routes within organization context */}
                  <Route element={<ProtectedRoute />}>
                    {/* Admin-only routes */}
                    <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
                      <Route path="admin/corpus-fund" element={withOrganizationContext(withLayout(AdminCorpusFund))()} />
                    </Route>
                    {/* Admin routes */}
                    <Route path="admin/dashboard" element={withOrganizationContext(withLayout(AdminDashboard))()} />
                    <Route path="admin/settings" element={withOrganizationContext(withLayout(AdminSettings))()} />
                    <Route path="admin/users" element={withOrganizationContext(withLayout(AdminUsers))()} />
                    <Route path="admin/units" element={withOrganizationContext(withLayout(AdminUnits))()} />
                    <Route path="admin/units/:id" element={withOrganizationContext(withLayout(UnitDetail))()} />
                    <Route path="admin/villas" element={<Navigate to="../admin/units" replace />} />
                    <Route path="admin/complaints" element={withOrganizationContext(withLayout(AdminComplaints))()} />
                    <Route path="admin/financials" element={withOrganizationContext(withLayout(AdminFinancials))()} />
                    <Route path="admin/visitors" element={withOrganizationContext(withLayout(AdminVisitors))()} />
                    <Route path="admin/activity" element={withOrganizationContext(withLayout(AdminActivity))()} />
                    <Route path="admin/reports" element={withOrganizationContext(withLayout(AdminReports))()} />
                    <Route path="admin/membership" element={withOrganizationContext(withLayout(AdminMembership))()} />
                    <Route path="admin/rules" element={withOrganizationContext(withLayout(AdminRules))()} />
                    <Route path="admin/announcements" element={withOrganizationContext(withLayout(AdminAnnouncements))()} />
                    <Route path="admin/rules/new" element={withOrganizationContext(withLayout(RuleForm))()} />
                    <Route path="admin/rules/:id/edit" element={withOrganizationContext(withLayout(RuleForm))()} />
                    <Route path="admin/announcements/new" element={withOrganizationContext(withLayout(AnnouncementForm))()} />
                    <Route path="admin/announcements/:id/edit" element={withOrganizationContext(withLayout(AnnouncementForm))()} />
                    <Route path="admin/support-tickets" element={withOrganizationContext(withLayout(AdminSupportTickets))()} />

                    {/* Owner routes */}
                    <Route path="owner/dashboard" element={withOrganizationContext(withLayout(OwnerDashboard))()} />
                    <Route path="owner/visitors" element={withOrganizationContext(withLayout(OwnerVisitors))()} />
                    <Route path="owner/profile" element={withOrganizationContext(withLayout(OwnerProfile))()} />
                    <Route path="owner/my-units" element={withOrganizationContext(withLayout(MyVillas))()} />
                    <Route path="owner/unit/:villaId" element={withOrganizationContext(withLayout(VillaDetail))()} />
                    <Route path="owner/payments" element={withOrganizationContext(withLayout(Payments))()} />
                    <Route path="owner/complaints" element={withOrganizationContext(withLayout(Complaints))()} />
                    <Route path="owner/rules" element={withOrganizationContext(withLayout(OwnerRules))()} />
                    <Route path="owner/announcements" element={withOrganizationContext(withLayout(OwnerAnnouncements))()} />
                    <Route path="owner/vehicles" element={withOrganizationContext(withLayout(Vehicles))()} />

                    {/* Directory page accessible to Admin and Owner */}
                    <Route path="directory" element={withOrganizationContext(DirectoryPageWithLayout)()} />
                    {/* Support page accessible to all roles */}
                    <Route path="support" element={withOrganizationContext(withLayout(Support))()} />
                    
                    {/* Guard routes */}
                    <Route path="guard/dashboard" element={withOrganizationContext(withLayout(GuardDashboard))()} />
                    <Route path="guard/visitors" element={withOrganizationContext(withLayout(GuardVisitors))()} />
                    <Route path="guard/visitor-search" element={withOrganizationContext(withLayout(VisitorSearch))()} />
                    <Route path="guard/villas" element={withOrganizationContext(withLayout(GuardVillas))()} />
                    <Route path="guard/vehicles" element={withOrganizationContext(withLayout(GuardVehicles))()} />
                    <Route path="guard/rules" element={withOrganizationContext(withLayout(GuardRules))()} />
                    <Route path="guard/announcements" element={withOrganizationContext(withLayout(GuardAnnouncements))()} />

                    {/* Maintenance Routes */}
                    <Route path="maintenance/dashboard" element={withOrganizationContext(withMaintenanceLayout(MaintenanceDashboard))()} />
                    <Route path="maintenance/complaints" element={withOrganizationContext(withMaintenanceLayout(MaintenanceComplaints))()} />
                    <Route path="maintenance/rules" element={withOrganizationContext(withMaintenanceLayout(MaintenanceRules))()} />
                    <Route path="maintenance/announcements" element={withOrganizationContext(withMaintenanceLayout(MaintenanceAnnouncements))()} />
                  </Route>
                </Route>

                {/* Regular protected routes without organization context */}
                <Route element={
                  <OrganizationContext.Provider value={null}>
                    <ProtectedRoute />
                  </OrganizationContext.Provider>
                }>
                  {/* Admin-only routes */}
                  <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
                    <Route path="/admin/corpus-fund" element={<AdminCorpusFundWithLayout />} />
                  </Route>
                  {/* Admin routes */}
                  <Route path="/admin/dashboard" element={<AdminDashboardWithLayout />} />
                  <Route path="/admin/settings" element={<AdminSettingsWithLayout />} />
                  <Route path="/admin/users" element={<AdminUsersWithLayout />} />
                  <Route path="/admin/units" element={<AdminUnitsWithLayout />} />
                  <Route path="/admin/units/:id" element={withLayout(UnitDetail)()} />
                  <Route path="/admin/villas" element={<Navigate to="/admin/units" replace />} />
                  <Route path="/admin/complaints" element={<AdminComplaintsWithLayout />} />
                  <Route path="/admin/financials" element={<AdminFinancialsWithLayout />} />
                  <Route path="/admin/visitors" element={<AdminVisitorsWithLayout />} />
                  <Route path="/admin/activity" element={<AdminActivityWithLayout />} />
                  <Route path="/admin/reports" element={<AdminReportsWithLayout />} />
                  <Route path="/admin/membership" element={<AdminMembershipWithLayout />} />
                  <Route path="/admin/rules" element={<AdminRulesWithLayout />} />
                  <Route path="/admin/announcements" element={<AdminAnnouncementsWithLayout />} />
                  <Route path="/admin/rules/new" element={<RuleFormWithLayout />} />
                  <Route path="/admin/rules/:id/edit" element={<RuleFormWithLayout />} />
                  <Route path="/admin/announcements/new" element={<AnnouncementFormWithLayout />} />
                  <Route path="/admin/announcements/:id/edit" element={<AnnouncementFormWithLayout />} />
                  <Route path="/admin/support-tickets" element={<AdminSupportTicketsWithLayout />} />

                  {/* Guard routes */}
                  <Route path="/guard/dashboard" element={<GuardDashboardWithLayout />} />
                  <Route path="/guard/visitors" element={<GuardVisitorsWithLayout />} />
                  <Route path="/guard/visitor-search" element={<VisitorSearchWithLayout />} />
                  <Route path="/guard/villas" element={<GuardVillasWithLayout />} />
                  <Route path="/guard/vehicles" element={<GuardVehiclesWithLayout />} />
                  <Route path="/guard/rules" element={<GuardRulesWithLayout />} />
                  <Route path="/guard/announcements" element={<GuardAnnouncementsWithLayout />} />

                  {/* Owner routes */}
                  <Route path="/owner/dashboard" element={<OwnerDashboardWithLayout />} />
                  <Route path="/owner/visitors" element={<OwnerVisitorsWithLayout />} />
                  <Route path="/owner/profile" element={<OwnerProfileWithLayout />} />
                  <Route path="/owner/my-units" element={<MyVillasWithLayout />} />
                  <Route path="/owner/unit/:villaId" element={<VillaDetailWithLayout />} />
                  <Route path="/owner/payments" element={<PaymentsWithLayout />} />
                  <Route path="/owner/complaints" element={<ComplaintsWithLayout />} />
                  <Route path="/owner/rules" element={<OwnerRulesWithLayout />} />
                  <Route path="/owner/announcements" element={<OwnerAnnouncementsWithLayout />} />
                  <Route path="/owner/vehicles" element={<VehiclesWithLayout />} />

                  {/* Directory page for non-org context */}
                  <Route path="/directory" element={<DirectoryPageWithLayout />} />
                  
                  {/* Superadmin routes (no org context needed) */}
                  <Route path="/superadmin/dashboard" element={<SuperadminDashboardWithLayout />} />
                  <Route path="/superadmin/tenants" element={<TenantsWithLayout />} />
                  <Route path="/superadmin/plans" element={<PlansWithLayout />} />
                  <Route path="/superadmin/subscriptions" element={<SubscriptionsWithLayout />} />
                  <Route path="/superadmin/subscriptions/:subscriptionId/transactions" element={<TransactionHistoryWithLayout />} />
                  <Route path="/superadmin/tenants/:tenantId/transactions" element={<TransactionHistoryWithLayout />} />
                  <Route path="/superadmin/settings" element={<SuperadminSettingsWithLayout />} />
                  <Route path="/superadmin/testimonials" element={<TestimonialsWithLayout />} />
                  <Route path="/superadmin/support-tickets" element={<SupportTicketsWithLayout />} />
                  <Route path="/superadmin/switch-user" element={<SwitchUserWithLayout />} />
                  
                  {/* Shared routes */}
                  <Route path="/profile" element={withLayout(Profile)()} />
                  <Route path="/change-password" element={withLayout(ChangePassword)()} />
                  {/* Directory page for non-org context */}
                  <Route path="/directory" element={<DirectoryPageWithLayout />} />
                  {/* Support page for non-org context */}
                  <Route path="/support" element={withLayout(Support)()} />
                </Route>

                {/* Maintenance Routes - Specially wrapped to avoid visitor API calls */}
                <Route element={<ProtectedRoute allowedRoles={['maintenance', 'admin']} />}>
                  <Route path="/maintenance/dashboard" element={<MaintenanceDashboardWithSpecialLayout />} />
                  <Route path="/maintenance/complaints" element={<MaintenanceComplaintsWithSpecialLayout />} />
                  <Route path="/maintenance/rules" element={<MaintenanceRulesWithSpecialLayout />} />
                  <Route path="/maintenance/announcements" element={<MaintenanceAnnouncementsWithSpecialLayout />} />
                </Route>

                {/* Catch-all route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </div>
          </VisitorProvider>
        </SettingsProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
